import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { trackEvent } from '../services/analytics';

// ─── Colors ───
const C = {
  text: '#4A2E1F',
  secondary: '#7A5A44',
  warmBg: '#FAF6F0',
  border: '#E8D9C8',
  green: '#5A8A60',
  red: '#B8564A',
  amber: '#B87A2E',
  olive: '#7E8B47',
  greenBg: '#E5EDE5',
  redBg: '#FCEAE8',
  amberBg: '#F5ECDB',
  oliveBg: '#F0EDE0',
};

const font = "'Plus Jakarta Sans', sans-serif";

// ─── Tier comparison ───
const TIER_ORDER = ['0', '1a', '1b', '1c', '2a', '2b', '3', '4', '5'];
function tierIndex(t) { const i = TIER_ORDER.indexOf(t); return i >= 0 ? i : 0; }

const TIER_CELEBRATIONS = {
  '3': { heading: 'AI Analysis Unlocked', text: "You've logged enough data for our AI to start finding real patterns. Here's what we found." },
  '4': { heading: 'Full Statistical Analysis Unlocked', text: 'Your insights are now backed by real statistical analysis.' },
  '5': { heading: 'Maximum Confidence Reached', text: "You've built an extensive gut health profile. Your analysis is now at peak accuracy." },
};

// ─── Category education ───
const CATEGORY_EDUCATION = {
  dairy: 'Dairy is one of the most common IBS triggers',
  'high-FODMAP': 'FODMAPs are fermentable carbs that can cause gas and bloating',
  gluten: 'Gluten sensitivity is common in IBS',
  nightshades: 'Nightshades can irritate sensitive guts',
  spicy: 'Spicy foods can trigger cramping and urgency',
  caffeine: 'Caffeine stimulates gut motility',
  alcohol: 'Alcohol irritates the gut lining',
  cruciferous: 'Cruciferous vegetables can cause gas',
  'high-fat/fried': 'High-fat foods slow digestion and can cause discomfort',
  'artificial sweeteners': 'Sugar alcohols are poorly absorbed and can cause bloating',
};

// ─── Shared components ───

function Card({ children, borderColor, bg, style }) {
  return (
    <div className="card" style={{
      borderLeft: borderColor ? `4px solid ${borderColor}` : undefined,
      background: bg || undefined,
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardTitle({ icon, children, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
      {icon}
      <h3 style={{ margin: 0, fontSize: '15px', fontFamily: font, color: color || C.text }}>{children}</h3>
    </div>
  );
}

function Pill({ children, bg, color, border }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: '12px', padding: '3px 10px',
      borderRadius: '14px', background: bg || C.border, color: color || C.text,
      border: border ? `1px solid ${border}` : undefined, fontWeight: '500',
    }}>
      {children}
    </span>
  );
}

function ProgressBar({ label, count, target, last = false }) {
  const pct = target > 0 ? Math.min(100, (count / target) * 100) : 0;
  const done = count >= target;
  return (
    <div style={{ marginBottom: last ? 0 : '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '14px', color: C.text, fontWeight: '500' }}>{label}</span>
        <span style={{ fontSize: '14px', color: done ? C.green : C.secondary, fontWeight: '600' }}>
          {done ? '✓' : `${count} / ${target}`}
        </span>
      </div>
      <div style={{ height: '6px', borderRadius: '3px', background: C.border, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, borderRadius: '3px',
          background: done ? C.green : C.olive, transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

function CTAButton({ onClick, children }) {
  return (
    <button className="btn btn-primary" onClick={onClick} style={{ width: '100%', marginBottom: '16px' }}>
      {children}
    </button>
  );
}

function bristolColor(b) {
  if (b >= 3 && b <= 4) return C.green;
  if (b === 2 || b === 5) return C.amber;
  return C.red;
}

// ─── Tier-specific views ───

function Tier0View({ navigate }) {
  const [sampleExpanded, setSampleExpanded] = useState(false);
  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '20px', color: C.text, fontWeight: '700', fontFamily: font }}>
          Your insights will appear here
        </h2>
        <p style={{ margin: 0, fontSize: '14px', color: C.secondary, lineHeight: '1.5' }}>
          Here's how it works
        </p>
      </div>

      <Card>
        {[
          { step: '1', label: 'Log your meals', desc: 'Snap a photo or type what you ate' },
          { step: '2', label: 'Track bowel movements', desc: 'Log Bristol score and symptoms' },
          { step: '3', label: 'Get insights', desc: 'We connect the dots for you' },
        ].map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '12px 0',
            borderBottom: i < 2 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '16px', flexShrink: 0,
              background: C.oliveBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', fontWeight: '700', color: C.olive,
            }}>
              {s.step}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: C.text }}>{s.label}</div>
              <div style={{ fontSize: '13px', color: C.secondary }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </Card>

      <SampleReport expanded={sampleExpanded} onToggle={() => setSampleExpanded(!sampleExpanded)} />

      <CTAButton onClick={() => navigate('/log')}>Log your first meal</CTAButton>
    </>
  );
}

function Tier1AView({ data, navigate }) {
  const { ingredientProfile, categoryExposure } = data;
  return (
    <>
      {/* Food Profile */}
      <Card borderColor={C.olive}>
        <CardTitle color={C.olive} icon={null}>Your Food Profile</CardTitle>
        <p style={{ margin: '0 0 12px', fontSize: '14px', color: C.text, lineHeight: '1.5' }}>
          You've eaten <strong>{ingredientProfile?.uniqueCount || 0}</strong> unique ingredients across <strong>{ingredientProfile?.totalMeals || 0}</strong> meal{ingredientProfile?.totalMeals !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {(ingredientProfile?.topIngredients || []).slice(0, 15).map((ing, i) => (
            <Pill key={i} bg={C.border} color={C.text}>{ing.name}{ing.count > 1 ? ` (${ing.count})` : ''}</Pill>
          ))}
        </div>
      </Card>

      {/* Trigger Categories */}
      {categoryExposure && categoryExposure.length > 0 && (
        <Card borderColor={C.amber}>
          <CardTitle color={C.amber} icon={null}>Trigger Categories in Your Diet</CardTitle>
          {categoryExposure.map((cat, i) => (
            <div key={i} style={{ marginBottom: i < categoryExposure.length - 1 ? '12px' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Pill bg={C.amberBg} color={C.amber}>{cat.category} ({cat.count})</Pill>
              </div>
              {CATEGORY_EDUCATION[cat.category] && (
                <p style={{ margin: 0, fontSize: '13px', color: C.secondary, lineHeight: '1.4', paddingLeft: '4px' }}>
                  {CATEGORY_EDUCATION[cat.category]}
                </p>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* What's Missing */}
      <Card borderColor={C.secondary} bg={C.warmBg}>
        <p style={{ margin: '0 0 12px', fontSize: '14px', color: C.text, lineHeight: '1.5' }}>
          Log a bowel movement so we can start connecting your food to how you feel
        </p>
        <CTAButton onClick={() => navigate('/log')}>Log a bowel movement</CTAButton>
      </Card>
    </>
  );
}

function Tier1BView({ data, navigate }) {
  const { bristolStats, symptomFrequency, checkInCorrelation } = data;
  const latest = bristolStats?.trend?.[bristolStats.trend.length - 1] || null;
  const latestBristol = latest?.bristol || (bristolStats?.average ? Math.round(bristolStats.average) : null);

  return (
    <>
      {/* Gut Health Snapshot */}
      <Card borderColor={latestBristol ? bristolColor(latestBristol) : C.secondary}>
        <CardTitle>Gut Health Snapshot</CardTitle>
        {latestBristol && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{
              fontSize: '28px', fontWeight: '700', color: bristolColor(latestBristol), fontFamily: font,
            }}>
              Bristol {latestBristol}
            </span>
            <span style={{ fontSize: '14px', color: C.secondary, marginLeft: '8px' }}>
              — {bristolStats.education?.[String(latestBristol)] || ''}
            </span>
          </div>
        )}
        {symptomFrequency?.symptoms?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {symptomFrequency.symptoms.map((s, i) => (
              <Pill key={i} bg={C.redBg} color={C.red}>{s.name}</Pill>
            ))}
          </div>
        )}
      </Card>

      {/* Symptoms */}
      {symptomFrequency?.symptoms?.length > 0 && (
        <Card borderColor={C.red}>
          <CardTitle color={C.red}>Your Symptoms</CardTitle>
          {symptomFrequency.symptoms.map((s, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: i < symptomFrequency.symptoms.length - 1 ? `1px solid ${C.border}` : 'none',
            }}>
              <span style={{ fontSize: '14px', color: C.text, textTransform: 'capitalize' }}>{s.name}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: C.red }}>{s.count}×</span>
            </div>
          ))}
        </Card>
      )}

      {/* Daily Context */}
      <DailyContextCard checkIn={checkInCorrelation} />

      {/* What's Missing */}
      <Card borderColor={C.secondary} bg={C.warmBg}>
        <p style={{ margin: '0 0 12px', fontSize: '14px', color: C.text, lineHeight: '1.5' }}>
          Log what you've been eating so we can look for connections
        </p>
        <CTAButton onClick={() => navigate('/log')}>Log a meal</CTAButton>
      </Card>
    </>
  );
}

function Tier1CView({ data, navigate }) {
  const { timeline, checkInCorrelation, progress, mealsCount, poopsCount } = data;

  // Collect all ingredients in window for poops
  const windowIngredients = [];
  (timeline || []).forEach(e => {
    if (e.type === 'poop' && e.mealsInWindow?.length > 0) {
      e.mealsInWindow.forEach(m => {
        (m.ingredients || []).forEach(ing => {
          if (!windowIngredients.find(w => w.name === ing)) {
            const isTrigger = (m.categories || []).length > 0;
            windowIngredients.push({ name: ing, isTrigger });
          }
        });
      });
    }
  });

  return (
    <>
      {/* Timeline */}
      <TimelineCard timeline={timeline} />

      {/* Ingredients in Window */}
      {windowIngredients.length > 0 && (
        <Card borderColor={C.amber}>
          <CardTitle color={C.amber}>Ingredients in the Window</CardTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {windowIngredients.map((ing, i) => (
              <Pill key={i}
                bg={ing.isTrigger ? C.amberBg : C.greenBg}
                color={ing.isTrigger ? C.amber : C.green}
              >
                {ing.isTrigger ? '⚠️ ' : '✅ '}{ing.name}
              </Pill>
            ))}
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: C.secondary, fontStyle: 'italic' }}>
            These were in your system when symptoms hit
          </p>
        </Card>
      )}

      <DailyContextCard checkIn={checkInCorrelation} />

      {/* Disclaimer + Progress */}
      <Card bg={C.warmBg}>
        <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.secondary, lineHeight: '1.5', fontStyle: 'italic' }}>
          This is early data — not enough to draw conclusions yet. But we're tracking it. Keep logging to unlock pattern detection.
        </p>
        <ProgressBar label="Meals" count={mealsCount} target={progress?.nextTierMeals || 5} />
        <ProgressBar label="Bowel movements" count={poopsCount} target={progress?.nextTierPoops || 3} last />
      </Card>
    </>
  );
}

function Tier2AView({ data, navigate }) {
  const { mealDiff, categoryExposure } = data;

  return (
    <>
      <Tier1CView data={data} navigate={navigate} />

      {/* Meal Comparison */}
      {mealDiff && (mealDiff.hasBadBMs || mealDiff.hasGoodBMs) && (
        <Card borderColor={C.amber}>
          <CardTitle color={C.amber}>Meal Comparison</CardTitle>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: C.red, marginBottom: '8px' }}>Before bad BMs</div>
              {(mealDiff.uniqueToBad || []).slice(0, 8).map((ing, i) => (
                <div key={i} style={{ fontSize: '13px', color: C.text, padding: '2px 0' }}>
                  ⚠️ {ing.name} ({ing.count}×)
                </div>
              ))}
              {(!mealDiff.uniqueToBad || mealDiff.uniqueToBad.length === 0) && (
                <p style={{ fontSize: '13px', color: C.secondary, margin: 0 }}>No unique ingredients yet</p>
              )}
            </div>
            <div style={{ width: '1px', background: C.border }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: C.green, marginBottom: '8px' }}>Before good BMs</div>
              {(mealDiff.uniqueToGood || []).slice(0, 8).map((ing, i) => (
                <div key={i} style={{ fontSize: '13px', color: C.text, padding: '2px 0' }}>
                  ✅ {ing.name} ({ing.count}×)
                </div>
              ))}
              {(!mealDiff.uniqueToGood || mealDiff.uniqueToGood.length === 0) && (
                <p style={{ fontSize: '13px', color: C.secondary, margin: 0 }}>No unique ingredients yet</p>
              )}
            </div>
          </div>
          {mealDiff.uniqueToBad?.length > 0 && (
            <p style={{ margin: '12px 0 0', fontSize: '13px', color: C.amber, fontWeight: '600' }}>
              Early suspects: {mealDiff.uniqueToBad.slice(0, 5).map(i => i.name).join(', ')}
            </p>
          )}
        </Card>
      )}

      {/* Category Exposure */}
      {categoryExposure && categoryExposure.length > 0 && (
        <Card borderColor={C.olive}>
          <CardTitle color={C.olive}>Category Exposure</CardTitle>
          {categoryExposure.map((cat, i) => (
            <div key={i} style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', color: C.text }}>{cat.category}</span>
                <span style={{ fontSize: '13px', color: C.secondary }}>{cat.percentage}%</span>
              </div>
              <div style={{ height: '8px', borderRadius: '4px', background: C.border, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${cat.percentage}%`, borderRadius: '4px',
                  background: cat.percentage > 75 ? C.amber : C.olive, transition: 'width 0.3s',
                }} />
              </div>
            </div>
          ))}
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: C.secondary, fontStyle: 'italic' }}>
            Categories in most meals are harder to isolate — variety helps us help you
          </p>
        </Card>
      )}
    </>
  );
}

function Tier2BView({ data, navigate }) {
  const { bristolStats, symptomFrequency, checkInCorrelation } = data;

  return (
    <>
      <Tier2AView data={data} navigate={navigate} />

      {/* Bristol Trend */}
      {bristolStats?.trend?.length >= 3 && (
        <Card borderColor={C.olive}>
          <CardTitle color={C.olive}>Bristol Trend</CardTitle>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'end', marginBottom: '12px', flexWrap: 'wrap' }}>
            {bristolStats.trend.map((t, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '14px',
                  background: bristolColor(t.bristol) + '20',
                  border: `2px solid ${bristolColor(t.bristol)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '700', color: bristolColor(t.bristol),
                }}>
                  {t.bristol}
                </div>
              </div>
            ))}
          </div>
          {bristolStats.trendLabel && (
            <p style={{
              margin: 0, fontSize: '13px', fontWeight: '600',
              color: bristolStats.trendDirection === 'improving' ? C.green
                : bristolStats.trendDirection === 'worsening' ? C.red : C.secondary,
            }}>
              {bristolStats.trendLabel}
            </p>
          )}
        </Card>
      )}

      {/* Top Symptoms */}
      {symptomFrequency?.symptoms?.length > 0 && (
        <Card borderColor={C.red}>
          <CardTitle color={C.red}>Top Symptoms</CardTitle>
          {symptomFrequency.symptoms.map((s, i) => (
            <div key={i} style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', color: C.text, textTransform: 'capitalize' }}>{s.name}</span>
                <span style={{ fontSize: '13px', color: C.secondary }}>{s.count}×</span>
              </div>
              <div style={{ height: '6px', borderRadius: '3px', background: C.border, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, (s.count / (symptomFrequency.totalPoops || 1)) * 100)}%`,
                  borderRadius: '3px', background: C.red, transition: 'width 0.3s',
                }} />
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Lifestyle Patterns */}
      {checkInCorrelation?.observations?.length > 0 && (
        <Card borderColor={C.amber}>
          <CardTitle color={C.amber}>Lifestyle Patterns</CardTitle>
          {checkInCorrelation.observations.map((obs, i) => (
            <div key={i} style={{
              padding: '8px 0',
              borderBottom: i < checkInCorrelation.observations.length - 1 ? `1px solid ${C.border}` : 'none',
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: C.text, lineHeight: '1.5' }}>
                {obs.observation}
              </p>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

function Tier3PlusView({ data, navigate }) {
  const { mealsCount, poopsCount } = data;
  // Determine next unlock targets
  const nextMeals = data.tier === '3' ? 10 : 30;
  const nextPoops = data.tier === '3' ? 5 : 20;

  return (
    <>
      <Tier2BView data={data} navigate={navigate} />

      <Card borderColor={C.olive} bg={C.warmBg}>
        <CardTitle color={C.olive}>AI Analysis</CardTitle>
        {data.earlyAnalysis ? (
          <div>
            {data.earlyAnalysis.headline && (
              <p style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: C.text, lineHeight: '1.5' }}>
                {data.earlyAnalysis.headline}
              </p>
            )}
            {data.earlyAnalysis.suspects?.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: C.amber, marginBottom: '6px' }}>Watching closely:</div>
                {data.earlyAnalysis.suspects.map((s, i) => (
                  <div key={i} style={{ fontSize: '13px', color: C.text, padding: '2px 0', lineHeight: '1.5' }}>
                    <strong>{s.name}</strong> — {s.reason}
                  </div>
                ))}
              </div>
            )}
            {data.earlyAnalysis.lifestyleNote && (
              <p style={{ margin: '0 0 8px', fontSize: '13px', color: C.secondary, lineHeight: '1.5' }}>
                {data.earlyAnalysis.lifestyleNote}
              </p>
            )}
            {data.earlyAnalysis.actionItem && (
              <p style={{ margin: 0, fontSize: '13px', color: C.olive, fontWeight: '600', lineHeight: '1.5' }}>
                Try this week: {data.earlyAnalysis.actionItem}
              </p>
            )}
          </div>
        ) : (
          <div>
            <p style={{ margin: '0 0 12px', fontSize: '14px', color: C.text, lineHeight: '1.5' }}>
              Full AI analysis unlocking at {nextMeals} meals + {nextPoops} poops
            </p>
            <ProgressBar label="Meals" count={mealsCount} target={nextMeals} />
            <ProgressBar label="Bowel movements" count={poopsCount} target={nextPoops} last />
          </div>
        )}
      </Card>
    </>
  );
}

// ─── Shared sub-components ───

function TimelineCard({ timeline }) {
  if (!timeline || timeline.length === 0) return null;

  return (
    <Card borderColor={C.olive}>
      <CardTitle color={C.olive}>Your Timeline</CardTitle>
      <div>
        {timeline.map((entry, i) => {
          const time = new Date(entry.timestamp).toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          });

          if (entry.type === 'meal') {
            return (
              <div key={i} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🍽</span>
                  <span style={{ fontSize: '12px', color: C.secondary }}>{time}</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: C.text }}>{entry.name}</span>
                </div>
                {entry.categories?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', marginLeft: '28px' }}>
                    {entry.categories.map((cat, j) => (
                      <Pill key={j} bg={C.amberBg} color={C.amber}>{cat}</Pill>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Poop entry
          const bc = entry.bristol ? bristolColor(entry.bristol) : C.secondary;
          return (
            <div key={i}>
              {/* Connector if in window */}
              {entry.inWindow && entry.hoursAfterMeal && (
                <div style={{
                  textAlign: 'center', padding: '4px 0', fontSize: '12px',
                  color: entry.hoursAfterMeal >= 6 && entry.hoursAfterMeal <= 36 ? C.amber : C.secondary,
                }}>
                  ↕ {entry.hoursAfterMeal}h
                  {entry.hoursAfterMeal >= 6 && entry.hoursAfterMeal <= 36
                    ? ' ⚠️ Within digestion window'
                    : ' — Outside typical window'}
                </div>
              )}
              <div style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>💩</span>
                  <span style={{ fontSize: '12px', color: C.secondary }}>{time}</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: bc }}>
                    Bristol {entry.bristol} — {entry.bristolLabel || ''}
                  </span>
                </div>
                {entry.symptoms?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', marginLeft: '28px' }}>
                    {entry.symptoms.map((s, j) => (
                      <Pill key={j} bg={C.redBg} color={C.red}>{s}</Pill>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DailyContextCard({ checkIn }) {
  if (!checkIn || !checkIn.latestLog) return null;
  const log = checkIn.latestLog;
  const fields = [
    log.stressLevel && `Stress: ${log.stressLevel}`,
    log.sleepQuality && `Sleep: ${log.sleepQuality}`,
    log.hydration && `Hydration: ${log.hydration}`,
    log.exerciseLevel && `Exercise: ${log.exerciseLevel}`,
    log.alcoholIntake && log.alcoholIntake !== 'none' && `Alcohol: ${log.alcoholIntake}`,
  ].filter(Boolean);

  if (fields.length === 0) return null;

  return (
    <Card borderColor={C.secondary}>
      <CardTitle>Daily Context</CardTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {fields.map((f, i) => (
          <Pill key={i} bg={C.warmBg} color={C.secondary}>{f}</Pill>
        ))}
      </div>
    </Card>
  );
}

function SampleReport({ expanded, onToggle }) {
  return (
    <div className="card" style={{ border: '2px dashed #D8CEBC', background: '#FDFCF8' }}>
      <div onClick={onToggle} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontFamily: font, color: C.text }}>
            Preview: What your report will look like
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: C.secondary }}>
            Real analysis from a user who tracked for 2 weeks
          </p>
        </div>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.secondary} strokeWidth="2" strokeLinecap="round"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {expanded && (
        <div style={{ marginTop: '16px', borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
          <div style={{ background: C.warmBg, borderLeft: `4px solid ${C.olive}`, padding: '12px 14px', borderRadius: '8px', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: C.text, lineHeight: '1.5' }}>
              Dairy appears to be your main trigger, while many other common IBS culprits like gluten and high-FODMAP foods seem safe for you.
            </p>
          </div>
          <div style={{ background: '#FFF8F6', borderLeft: `4px solid ${C.red}`, padding: '12px 14px', borderRadius: '8px', marginBottom: '12px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: C.red }}>Confirmed Triggers</p>
            <p style={{ margin: 0, fontSize: '13px', color: C.text, lineHeight: '1.5' }}>
              Your data strongly suggests dairy is a major trigger — you're 160 times more likely to have symptoms after eating dairy products.
            </p>
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              <Pill bg={C.redBg} color={C.red}>Dairy (160x)</Pill>
              <Pill bg={C.redBg} color={C.red}>Milk (5.2x)</Pill>
            </div>
          </div>
          <div style={{ background: '#F4F7F0', borderLeft: `4px solid ${C.green}`, padding: '12px 14px', borderRadius: '8px', marginBottom: '12px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: C.green }}>Safe Foods</p>
            <p style={{ margin: 0, fontSize: '13px', color: C.text, lineHeight: '1.5' }}>
              Gluten, high-FODMAP foods, nightshades, olive oil, and lemon all showed no association with your symptoms.
            </p>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: '11px', color: C.secondary, textAlign: 'center', fontStyle: 'italic' }}>
            This is a real report generated from tracked data. Yours will be personalized to your food and lifestyle patterns.
          </p>
        </div>
      )}
    </div>
  );
}

function CelebrationCard({ celebration }) {
  const confettiColors = ['#7E8B47', '#5A8A60', '#B87A2E', '#B8564A', '#E8C547', '#C4783C'];
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    color: confettiColors[i % confettiColors.length],
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.5}s`,
    duration: `${1.5 + Math.random() * 1.5}s`,
    size: 4 + Math.random() * 6,
    drift: -100 + Math.random() * 200,
    rotation: Math.random() * 720,
  }));

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '16px', marginBottom: '16px' }}>
      {/* Confetti layer */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
        {particles.map(p => (
          <div key={p.id} style={{
            position: 'absolute', top: '-10px', left: p.left,
            width: `${p.size}px`, height: `${p.size * (0.5 + Math.random())}px`,
            backgroundColor: p.color, borderRadius: '2px',
            animation: `confettiFall ${p.duration} ${p.delay} ease-out forwards`,
            opacity: 0,
            '--drift': `${p.drift}px`,
            '--rotation': `${p.rotation}deg`,
          }} />
        ))}
      </div>
      {/* Content */}
      <div style={{
        background: C.olive, color: '#fff', padding: '24px 20px',
        textAlign: 'center', position: 'relative', zIndex: 2,
        animation: 'celebrationIn 0.5s ease-out',
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', fontFamily: font, color: '#fff' }}>
          {celebration.heading}
        </h3>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5', opacity: 0.9 }}>
          {celebration.text}
        </p>
      </div>
      <style>{`
        @keyframes celebrationIn {
          0% { opacity: 0; transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes confettiFall {
          0% { opacity: 1; transform: translateY(0) translateX(0) rotate(0deg); }
          100% { opacity: 0; transform: translateY(300px) translateX(var(--drift)) rotate(var(--rotation)); }
        }
      `}</style>
    </div>
  );
}

// ─── Main component ───

function Insights() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  const [celebration, setCelebration] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/insights/snapshot');
      setSnapshot(res.data);
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    trackEvent('insights_viewed');
  }, [fetchData]);

  // Check for tier celebration
  useEffect(() => {
    if (!snapshot) return;
    const currentTier = snapshot.tier || '0';
    const lastSeen = localStorage.getItem('lastSeenInsightTier') || '0';
    const currentIdx = tierIndex(currentTier);
    const lastIdx = tierIndex(lastSeen);
    if (currentIdx > lastIdx) {
      // Find highest celebration tier crossed
      for (const t of ['5', '4', '3']) {
        if (currentIdx >= tierIndex(t) && lastIdx < tierIndex(t) && TIER_CELEBRATIONS[t]) {
          setCelebration(TIER_CELEBRATIONS[t]);
          break;
        }
      }
      localStorage.setItem('lastSeenInsightTier', currentTier);
    }
  }, [snapshot]);

  const tier = snapshot?.tier || '0';

  const renderTier = () => {
    if (!snapshot) return null;
    const data = { ...snapshot, navigate };

    switch (tier) {
      case '0': return <Tier0View navigate={navigate} />;
      case '1a': return <Tier1AView data={data} navigate={navigate} />;
      case '1b': return <Tier1BView data={data} navigate={navigate} />;
      case '1c': return <Tier1CView data={data} navigate={navigate} />;
      case '2a': return <Tier2AView data={data} navigate={navigate} />;
      case '2b': return <Tier2BView data={data} navigate={navigate} />;
      default: return <Tier3PlusView data={data} navigate={navigate} />;
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Insights</h1>
      </div>
      <div className="container">
        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : (
          <>
            {celebration && <CelebrationCard celebration={celebration} />}
            {tier !== '0' && (
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <TierBadge tier={tier} />
              </div>
            )}
            {renderTier()}
          </>
        )}
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const tierLabels = {
    '1a': 'Food Profile',
    '1b': 'Gut Snapshot',
    '1c': 'First Pair',
    '2a': 'Building Data',
    '2b': 'First Signals',
    '3': 'Early Patterns',
    '4': 'Solid Analysis',
    '5': 'Full Analysis',
  };
  const tierColors = {
    '1a': C.olive, '1b': C.olive, '1c': C.olive,
    '2a': C.amber, '2b': C.amber,
    '3': C.olive, '4': C.green, '5': C.green,
  };
  const color = tierColors[tier] || C.secondary;
  const label = tierLabels[tier] || tier;

  return (
    <span style={{
      display: 'inline-block', fontSize: '12px', fontWeight: '600',
      padding: '4px 12px', borderRadius: '20px',
      background: color + '18', color,
    }}>
      {label}
    </span>
  );
}

export default Insights;
